import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getAPIBaseURL, getUser } from '../services/helpers'
import { getData, patchData } from '../services/request'
import { COUNTRIES } from '../data/countries'
import logo from '../asset/images/logo.svg'
import {
  COMPANY_SIZES,
  INDUSTRIES,
  companyToFormState,
  formToCompanyPayload,
  inputClass,
  labelClass,
  needsEmployerOnboarding
} from './employerOnboardingUtils'

const STEP_LABELS = ['Company', 'About you', 'Contact', 'Launch']

const CompanyAvatar = ({ name, size = 'lg' }) => {
  const initial = (name || '').trim().charAt(0).toUpperCase()
  const sizeClass =
    size === 'lg'
      ? 'h-16 w-16 text-2xl ring-4 ring-white/10'
      : 'h-10 w-10 text-sm ring-2 ring-white/10'

  if (!initial) {
    return (
      <div
        className={`${sizeClass} flex shrink-0 items-center justify-center rounded-xl border border-dashed border-white/30 bg-white/5 text-white/35`}
        aria-hidden="true"
      >
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      </div>
    )
  }

  return (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 font-bold text-white shadow-lg shadow-primary-900/30`}
      aria-hidden="true"
    >
      {initial}
    </div>
  )
}

const PreviewSection = ({ title, icon, children, delay = 0 }) => (
  <section
    className="animate-slide-up opacity-0"
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
  >
    <div className="mb-2 flex items-center gap-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-50 text-primary-600">{icon}</span>
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{title}</h3>
    </div>
    {children}
  </section>
)

const EmptyValue = ({ label }) => (
  <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-3">
    <span className="text-sm italic text-slate-400">{label || 'Not provided'}</span>
  </div>
)

const PreviewRow = ({ label, value, emptyLabel = 'Not provided' }) => {
  const hasValue = value != null && String(value).trim() !== ''
  return (
    <div className="flex flex-col gap-0.5 border-b border-slate-100 py-2.5 last:border-0 sm:flex-row sm:gap-4">
      <dt className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400 sm:w-36">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm">
        {hasValue ? (
          <span className="text-slate-700">{value}</span>
        ) : (
          <span className="italic text-slate-400">{emptyLabel}</span>
        )}
      </dd>
    </div>
  )
}

const CompanyPreview = ({ form }) => {
  const locationParts = [form.location.city, form.location.state, form.location.country].filter(Boolean)
  const locationLabel = locationParts.join(', ')

  return (
    <div className="animate-scale-in overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60">
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-7 text-white">
        <div
          className="flex items-center gap-4 animate-slide-up opacity-0"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards' }}
        >
          <CompanyAvatar name={form.name} />
          <div className="min-w-0 flex-1">
            {form.name ? (
              <h2 className="text-2xl font-bold tracking-tight">{form.name}</h2>
            ) : (
              <p className="text-sm italic text-white/40">Company name not provided</p>
            )}
            {form.industry ? (
              <p className="mt-1.5 text-sm font-medium text-primary-100">{form.industry}</p>
            ) : (
              <p className="mt-1.5 text-sm italic text-white/35">Industry not provided</p>
            )}
          </div>
        </div>
        <div
          className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-300 animate-slide-up opacity-0"
          style={{ animationDelay: '140ms', animationFillMode: 'forwards' }}
        >
          {form.size ? (
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {form.size} employees
            </span>
          ) : (
            <span className="italic text-white/30">Company size not provided</span>
          )}
          {locationLabel ? (
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {locationLabel}
            </span>
          ) : (
            <span className="italic text-white/30">Location not provided</span>
          )}
          {form.website ? (
            <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
              </svg>
              {form.website.replace(/^https?:\/\//, '')}
            </span>
          ) : (
            <span className="italic text-white/30">Website not provided</span>
          )}
        </div>
      </div>

      <div className="space-y-6 px-6 py-6">
        <PreviewSection
          title="Company details"
          delay={180}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        >
          <dl className="rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-1">
            <PreviewRow label="Company name" value={form.name} emptyLabel="Company name not provided" />
            <PreviewRow label="Industry" value={form.industry} emptyLabel="Industry not provided" />
            <PreviewRow
              label="Company size"
              value={form.size ? `${form.size} employees` : ''}
              emptyLabel="Company size not provided"
            />
            <PreviewRow label="Website" value={form.website} emptyLabel="Website not provided" />
          </dl>
        </PreviewSection>

        <PreviewSection
          title="About"
          delay={220}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        >
          {form.description ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{form.description}</p>
          ) : (
            <EmptyValue label="Company description not provided" />
          )}
        </PreviewSection>

        <PreviewSection
          title="Location"
          delay={240}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        >
          <dl className="rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-1">
            <PreviewRow label="Country" value={form.location.country} emptyLabel="Country not provided" />
            <PreviewRow label="State / Region" value={form.location.state} emptyLabel="State or region not provided" />
            <PreviewRow label="City" value={form.location.city} emptyLabel="City not provided" />
            <PreviewRow label="Address" value={form.location.addressLine} emptyLabel="Address not provided" />
          </dl>
        </PreviewSection>

        <PreviewSection
          title="Hiring contact"
          delay={320}
          icon={
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          }
        >
          <dl className="rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-1">
            <PreviewRow label="Contact person" value={form.contactPersonName} emptyLabel="Contact person not provided" />
            <PreviewRow label="Role" value={form.contactPersonRole} emptyLabel="Role not provided" />
            <PreviewRow label="Email" value={form.contactEmail} emptyLabel="Email not provided" />
            <PreviewRow label="Phone" value={form.contactPhone} emptyLabel="Phone not provided" />
          </dl>
        </PreviewSection>
      </div>
    </div>
  )
}

const WelcomeHero = () => (
  <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-sky-500 px-6 py-8 text-white">
    <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 animate-float" />
    <div
      className="pointer-events-none absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-white/5 animate-float"
      style={{ animationDelay: '1s' }}
    />
    <div className="relative animate-slide-up">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse-soft" />
        Employer setup
      </div>
      <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Welcome to EmployMeGlobal</h2>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-primary-50/90">
        Set up your company profile in a few minutes. A complete profile helps you attract better candidates and get jobs approved faster.
      </p>
    </div>
  </div>
)

const EmployerOnboarding = () => {
  const navigate = useNavigate()
  const user = getUser()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [animKey, setAnimKey] = useState(0)
  const [direction, setDirection] = useState('forward')

  const [form, setForm] = useState({
    name: '',
    industry: '',
    size: '1-10',
    website: '',
    description: '',
    contactPersonName: '',
    contactPersonRole: 'HR',
    contactEmail: '',
    contactPhone: '',
    location: { country: '', state: '', city: '', addressLine: '' }
  })

  useEffect(() => {
    const isLoggedIn =
      typeof window !== 'undefined' &&
      (sessionStorage.getItem('employme-active') === '1' || !!localStorage.getItem('employmeUser'))
    if (!isLoggedIn) {
      navigate('/login', { replace: true })
      return
    }
    if (user?.role && user.role !== 'EMPLOYER') {
      navigate('/jobs', { replace: true })
      return
    }

    const fetchCompany = async () => {
      setLoading(true)
      const url = `${getAPIBaseURL()}/employme/employer/company`
      const res = await getData(url, {}, {})
      setLoading(false)
      if (res.error === '' && res.result?.status === 200) {
        const company = res.result.data?.data || res.result.data
        if (!needsEmployerOnboarding(company)) {
          navigate('/employer/dashboard', { replace: true })
          return
        }
        setForm(companyToFormState(company, user))
      }
    }

    fetchCompany()
  }, [navigate, user?.role])

  const onChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const onLocationChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      location: { ...prev.location, [name]: value }
    }))
  }

  const goToStep = (nextStep) => {
    setDirection(nextStep > step ? 'forward' : 'back')
    setAnimKey((k) => k + 1)
    setStep(nextStep)
    setError('')
    setInfo('')
  }

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!form.name.trim()) return 'Company name is required.'
      if (!form.industry.trim()) return 'Please select your industry.'
    }
    if (currentStep === 2) {
      if (!form.description.trim()) return 'Please add a short company description.'
      if (!form.location.country.trim()) return 'Please select your country.'
      if (!form.location.city.trim()) return 'Please enter your city.'
    }
    if (currentStep === 3) {
      if (!form.contactPersonName.trim()) return 'Contact person name is required.'
      if (!form.contactPersonRole.trim()) return 'Contact person role is required.'
      if (!form.contactPhone.trim()) return 'Contact phone is required.'
    }
    return ''
  }

  const saveCompany = async (nextAction) => {
    const validationError = nextAction !== 'back' ? validateStep(step) : ''
    if (validationError) {
      setError(validationError)
      return
    }

    setSaving(true)
    setError('')
    setInfo('')

    const complete = nextAction === 'dashboard' || nextAction === 'postJob'
    const payload = formToCompanyPayload(form, { complete })
    const url = `${getAPIBaseURL()}/employme/employer/company`
    const res = await patchData(url, { 'Content-Type': 'application/json' }, payload)
    setSaving(false)

    if (res.error !== '' || !res.result || res.result.status !== 200) {
      setError(res.result?.data?.data || res.result?.data?.message || 'Unable to save your company profile.')
      return
    }

    if (nextAction === 'nextStep') {
      setInfo('Saved.')
      goToStep(Math.min(step + 1, 4))
    } else if (nextAction === 'dashboard') {
      navigate('/employer/dashboard', { replace: true })
    } else if (nextAction === 'postJob') {
      navigate('/employer/jobs/new', { replace: true })
    }
  }

  const stepAnimClass = direction === 'forward' ? 'animate-slide-in-right' : 'animate-slide-up'

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50/30">
        <div className="mx-auto max-w-3xl px-4 py-10 animate-fade-in">
          <div className="mb-8 flex justify-center">
            <div className="h-10 w-40 animate-pulse-soft rounded-lg bg-slate-200" />
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/50">
            <div className="mb-6 flex items-center justify-between">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex flex-1 items-center">
                  <div className="h-9 w-9 animate-pulse-soft rounded-full bg-slate-200" />
                  {s < 4 && <div className="mx-2 h-px flex-1 bg-slate-200" />}
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <div className="h-6 w-48 animate-pulse-soft rounded-lg bg-slate-200" />
              <div className="h-4 w-full animate-pulse-soft rounded bg-slate-100" />
              <div className="h-11 w-full animate-pulse-soft rounded-xl bg-slate-100" />
            </div>
            <p className="mt-6 text-center text-sm text-slate-500">Preparing your onboarding...</p>
          </div>
        </div>
      </div>
    )
  }

  const progressPercent = ((step - 1) / (STEP_LABELS.length - 1)) * 100

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="mb-3 hidden items-center justify-between text-xs font-medium sm:flex">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1
          const isActive = step === stepNum
          const isDone = step > stepNum
          return (
            <span
              key={label}
              className={`transition-colors duration-300 ${
                isActive ? 'text-primary-600' : isDone ? 'text-emerald-600' : 'text-slate-400'
              }`}
            >
              {label}
            </span>
          )
        })}
      </div>
      <div className="relative h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="mt-4 flex items-center justify-between">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1
          const isActive = step === stepNum
          const isDone = step > stepNum
          return (
            <div key={label} className="flex flex-1 items-center">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                  isActive
                    ? 'scale-110 bg-primary-600 text-white shadow-md shadow-primary-500/30'
                    : isDone
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {isDone ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  stepNum
                )}
              </div>
              {stepNum < 4 && (
                <div className={`mx-1 h-px flex-1 transition-colors duration-300 sm:mx-2 ${isDone ? 'bg-emerald-200' : 'bg-slate-200'}`} />
              )}
            </div>
          )
        })}
      </div>
      <p className="mt-3 text-center text-xs text-slate-500 sm:hidden">
        Step {step} of {STEP_LABELS.length}: {STEP_LABELS[step - 1]}
      </p>
    </div>
  )

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div key={`step-${animKey}`} className={stepAnimClass}>
          <WelcomeHero />
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900">Your company</h1>
          <p className="mb-6 text-sm leading-relaxed text-slate-500">
            Tell candidates who you are. You can update these details anytime from your profile.
          </p>
          <div className="space-y-5">
            <div>
              <label className={labelClass}>Company name</label>
              <input name="name" value={form.name} onChange={onChange} className={inputClass} placeholder="Acme Corp" />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Industry</label>
                <select name="industry" value={form.industry} onChange={onChange} className={inputClass}>
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Company size</label>
                <select name="size" value={form.size} onChange={onChange} className={inputClass}>
                  {COMPANY_SIZES.map((s) => (
                    <option key={s} value={s}>{s} employees</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Website (optional)</label>
              <input
                name="website"
                value={form.website}
                onChange={onChange}
                className={inputClass}
                placeholder="https://yourcompany.com"
              />
            </div>
          </div>
        </div>
      )
    }

    if (step === 2) {
      return (
        <div key={`step-${animKey}`} className={stepAnimClass}>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900">About your company</h1>
          <p className="mb-6 text-sm leading-relaxed text-slate-500">
            Help job seekers understand your mission, culture, and what makes your team unique.
          </p>
          <div className="space-y-5">
            <div>
              <label className={labelClass}>Company description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={onChange}
                rows={5}
                placeholder="What does your company do? What kind of talent are you looking for?"
                className={`${inputClass} resize-y`}
              />
              <p className="mt-1.5 text-[11px] text-slate-400">
                {(form.description || '').trim().length}/500 characters recommended
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Country</label>
                <select
                  name="country"
                  value={form.location.country}
                  onChange={onLocationChange}
                  className={inputClass}
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>State / Region</label>
                <input
                  name="state"
                  value={form.location.state}
                  onChange={onLocationChange}
                  className={inputClass}
                  placeholder="e.g. Lagos"
                />
              </div>
              <div>
                <label className={labelClass}>City</label>
                <input
                  name="city"
                  value={form.location.city}
                  onChange={onLocationChange}
                  className={inputClass}
                  placeholder="e.g. Ikeja"
                />
              </div>
              <div>
                <label className={labelClass}>Address (optional)</label>
                <input
                  name="addressLine"
                  value={form.location.addressLine}
                  onChange={onLocationChange}
                  className={inputClass}
                  placeholder="Street address"
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (step === 3) {
      return (
        <div key={`step-${animKey}`} className={stepAnimClass}>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900">Hiring contact</h1>
          <p className="mb-6 text-sm leading-relaxed text-slate-500">
            Who should candidates and our team reach out to about your job posts?
          </p>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Contact person</label>
                <input
                  name="contactPersonName"
                  value={form.contactPersonName}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Role</label>
                <input
                  name="contactPersonRole"
                  value={form.contactPersonRole}
                  onChange={onChange}
                  className={inputClass}
                  placeholder="e.g. HR Manager"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                name="contactEmail"
                value={form.contactEmail}
                readOnly
                className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-500"
              />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input
                name="contactPhone"
                value={form.contactPhone}
                onChange={onChange}
                className={inputClass}
                placeholder="+234..."
              />
            </div>
            <div className="rounded-xl border border-sky-100 bg-sky-50/80 px-4 py-3 text-sm text-sky-800 animate-fade-in">
              <p className="font-medium">Verification documents</p>
              <p className="mt-1 text-xs leading-relaxed text-sky-700/80">
                You can upload CAC or business registration documents later from your profile to get your company verified faster.
              </p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div key={`step-${animKey}`} className={stepAnimClass}>
        <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900">You&apos;re all set!</h1>
        <p className="mb-6 text-sm leading-relaxed text-slate-500">
          Here&apos;s how your company will appear to candidates. Empty fields are marked so you know what to complete later.
        </p>
        <CompanyPreview form={form} />
        <div
          className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3 animate-slide-up opacity-0"
          style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
        >
          {[
            { icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', label: 'Post jobs' },
            { icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', label: 'Review applicants' },
            { icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', label: 'AI ranking' }
          ].map((item, i) => (
            <div
              key={item.label}
              className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3"
              style={{ animationDelay: `${480 + i * 80}ms` }}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
              </span>
              <span className="text-xs font-medium text-slate-700">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50/30">
      <header className="border-b border-slate-200/60 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img className="h-8 w-8" src={logo} alt="EmployMeGlobal" />
            <span className="text-lg font-semibold text-slate-900">EmployMeGlobal</span>
          </Link>
          <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            Employer onboarding
          </span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10 animate-fade-in">
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xl shadow-slate-200/50">
          <div className="h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-sky-500" />
          <div className="p-6 sm:p-8">
            {renderStepIndicator()}
            {error ? (
              <div className="mb-5 animate-slide-up rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            ) : null}
            {!error && info ? (
              <div className="mb-5 animate-slide-up rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {info}
              </div>
            ) : null}
            {renderStepContent()}
            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
              <button
                type="button"
                disabled={step === 1 || saving}
                onClick={() => goToStep(Math.max(step - 1, 1))}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40"
              >
                Back
              </button>
              <div className="flex flex-wrap justify-end gap-2">
                {step < 4 ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => saveCompany('nextStep')}
                    className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 hover:shadow-lg disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Continue'}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => saveCompany('dashboard')}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Go to dashboard'}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => saveCompany('postJob')}
                      className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 hover:shadow-lg disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Post your first job'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployerOnboarding
